import { Chart, HtmlEditor, PieChart } from "devextreme-react";
import { useEffect, useRef, useState } from "react";
import { get, post } from "../modules/server";
import { logAndNotifyError, logDebugSilently } from "../modules/logger";
import "devextreme/dist/css/dx.light.css";

import TreeView from "devextreme-react/tree-view";
import { SelectionChangedEvent } from "devextreme/ui/tree_view";
import { useGlobalData } from "../components/GlobalDataProvider";
import { ModuleTypeId } from "../types/DBTables";
import { ArgumentAxis, CommonAnnotationSettings, CommonSeriesSettings, Connector, Export, Height, Label, Legend, Series, Size, Title, Tooltip, ValueAxis } from "devextreme-react/chart";
import { Colors } from "../modules/Colors";
import { Card } from "react-bootstrap";
import DashBoardStatusIndicator from "../components/Dashboard/DashBoardStatusIndicator";
import { useNavigate } from "react-router-dom";
import { UserInfo } from "../types/user-info";
import { useDebounce } from "use-debounce";
import { useMediaQuery } from "react-responsive";

const Start = () => {
  const globalData = useGlobalData();
  const navigate = useNavigate();

  const [selectedBuildingIds, setSelectedBuildingIds] = useState<number[]>([]);
  const [treeviewData, setTreeviewData] = useState<any>(undefined);
  const [dashboardData, setDashboardData] = useState<any>(undefined);
  const showGetStarted = !globalData?.user?.dontShowGetStarted ? true : false;
  const refGetStartedShowedAt = useRef<Date | undefined>(undefined);
  const [debouncedSelectedBuildingIds] = useDebounce(selectedBuildingIds, 250);
  const requestIdRef = useRef(0);

  const isSmallScreen = useMediaQuery({ maxWidth: 576 });
  const treeRef = useRef(null);

  const customizeTooltip = (arg: any) => {
    const r = arg;
    let tt = "";
    for (let i = 0; i < arg.points.length; i++) {
      const p = arg.points[i];
      tt += `${Math.round(p.value / 10) * 10} ${p.seriesName} \n`;
    }
    arg.valueText = tt;
  };

  const customizePoint = (pointInfo: any) => {
    switch (pointInfo.argument) {
      case "Åtgärdade":
        return { color: Colors.GreenMedium };
      case "Ej åtgärdade":
        return { color: Colors.RedMedium };
    }
    return { color: "white" };
  };

  // const customizeLabel = (pointInfo: any) => {
  //   return <Label text={"Tjo"}></Label>;
  // };

  const customizeText = (annotationItem: any) => {
    annotationItem.text += " sdf";
    return annotationItem;
  };

  useEffect(() => {
    if (!globalData?.user) return;
    //Surpress showing the get started dialog if it has been shown within the last 9 seconds
    if ((!refGetStartedShowedAt.current || new Date().getTime() - refGetStartedShowedAt.current.getTime() > 9000) && showGetStarted) {
      refGetStartedShowedAt.current = new Date();
      setTimeout(() => {
        logDebugSilently("ShowGetStarted - " + new Date());
        PubSub.publish("ShowGetStarted");
        refGetStartedShowedAt.current = undefined;
      }, 1000);
    }
  }, [showGetStarted, globalData]);

  useEffect(() => {
    if (!globalData?.buildings || !globalData?.user) return;
    const områden = globalData.buildings?.reduce(
      (res, b) => {
        const modByggnadsData = b.modules.find((m) => m.moduleTypeId === ModuleTypeId.Byggnadsdata);
        const byggnadsData = JSON.parse(modByggnadsData?.data ? modByggnadsData.data : "{}");
        const omr = byggnadsData?.område ? byggnadsData.område : " Inget område angivit";
        if (!res.areas[omr]) res.areas[omr] = { id: omr, name: omr, selected: true, isArea: true, areaId: "alla" };

        res.buidings.push({ id: b.id, name: b.name, areaId: omr });
        return res;
      },
      { areas: {}, buidings: [] } as any
    );

    const buildingIds = Object.values(områden.buidings).map((b) => (b as any).id);

    setSelectedBuildingIds(buildingIds);

    const data = Object.values(områden.areas).concat(områden.buidings);
    data.push({ id: "alla", name: "Allt", selected: true, isArea: true, expanded: !isSmallScreen });
    logDebugSilently("SetTreeviewData");
    setTreeviewData(data);
    //The below comment tells the react-linter to ignore the dependency on globalData.buildings, because we want to run this effect only once when the component mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalData]);

  useEffect(() => {
    const generateDashboardData = (data: any) => {
      const ackBesparingar = [{ n: 0, kwh: 0, sek: 0, p: "" }] as any[];
      const lastPeriodsStatus = data.resultPerPeriod.slice(-3).map((g: any) => g);

      const statusFunktionsfel = data.resultPerPeriod.slice(-1).map((g: any) => ({
        period: g.period.name,
        upptäckta: g.antalFunktionsFelÅtgärdade + g.antalFunktionsFelEjÅtgärdade,
        åtgärdade: g.antalFunktionsFelÅtgärdade,
        kvar: g.antalFunktionsFelEjÅtgärdade,
      }));

      const resultLastBuildingRound = data.resultLastBuildingRound;
      const fördelningFunktionsfel = [];
      let hasFunktionsfel = false;
      if (resultLastBuildingRound) {
        fördelningFunktionsfel.push({ status: "Åtgärdade", antal: resultLastBuildingRound.antalFunktionsFelÅtgärdade });
        fördelningFunktionsfel.push({ status: "Ej åtgärdade", antal: resultLastBuildingRound.antalFunktionsFelEjÅtgärdade });
        hasFunktionsfel = resultLastBuildingRound.antalFunktionsFelÅtgärdade || resultLastBuildingRound.antalFunktionsFelEjÅtgärdade;
      }

      const ack = { kwh: 0, sek: 0 };
      for (let i = 0; i < data.resultPerPeriod.length; i++) {
        const aggPeriod = data.resultPerPeriod[i];
        ack.kwh += aggPeriod.driftOptimeringarEnergySaved + aggPeriod.funktionsFelEnergySaved;
        ack.sek += aggPeriod.sekSaved;
        ackBesparingar.push({ n: i + 1, kwh: ack.kwh, sek: ack.sek, p: aggPeriod.period.name });
      }

      return {
        ackBesparingar,
        lastPeriodsStatus,
        statusFunktionsfel: statusFunktionsfel.length ? statusFunktionsfel[0] : undefined,
        fördelningFunktionsfel,
        hasFunktionsfel,
      };
    };

    const fetchData = async (buildingIds: number[], requestId: number) => {
      if (!treeviewData) {
        logDebugSilently("Discarding Fetching DashboardData (" + buildingIds.length + "): " + buildingIds);
        return;
      }
      logDebugSilently("Fetching DashboardData (" + buildingIds.length + "): " + buildingIds);
      const response = await post("GetDashboardData", { buildingIds });

      if (!response) {
        logAndNotifyError("Failed to fetch dashboard data :" + response);
        return;
      }
      if (response?.success) {
        if (requestId !== requestIdRef.current) {
          logDebugSilently("Stale response ignored for requestId " + requestId);
          return;
        }
        logDebugSilently("Setting DashboardData (" + buildingIds.length + "): " + buildingIds);
        const ddData = generateDashboardData(response.data);
        logDebugSilently("Ack besp length:" + ddData.ackBesparingar.length);
        setDashboardData(ddData);
      }
    };

    if (!globalData?.buildings || !globalData?.user) return;

    const requestId = ++requestIdRef.current;
    fetchData(debouncedSelectedBuildingIds, requestId);
  }, [debouncedSelectedBuildingIds, globalData, treeviewData]);

  useEffect(() => {
    if (!globalData?.user) {
      navigate("/login");
    }
  }, [globalData, navigate]);

  // Automatically collapse nodes on small screens
  useEffect(() => {
    const treeViewInstance = (treeRef.current as any)?.instance;
    if (treeViewInstance) {
      if (isSmallScreen) {
        // Collapse all nodes
        treeViewInstance.collapseAll();
      } else {
        // Optionally expand all nodes on larger screens
        // Collapse all, then expand only the "Allt" node
        treeViewInstance.collapseAll();

        // Wait a moment to ensure the collapse finished (TreeView can be async-ish)
        setTimeout(() => {
          treeViewInstance.expandItem("alla");
        }, 50); // Slight delay ensures collapse finishes before expand
      }
    }
  }, [isSmallScreen]);

  if (!globalData?.user) {
    return <></>;
  }

  const hasAckBesparingar = dashboardData && dashboardData.ackBesparingar.length > 1 && dashboardData.ackBesparingar[dashboardData.ackBesparingar.length - 1].kwh > 0;
  const hasAckSEK = dashboardData && dashboardData.ackBesparingar.length > 1 && dashboardData.ackBesparingar[dashboardData.ackBesparingar.length - 1].sek > 0;

  const onSelectionChanged = (e: SelectionChangedEvent<any>) => {
    const selectedItems = e.component
      .getSelectedNodes()
      .map((node) => node.itemData)
      .filter((item) => !(item as any).isArea);
    const selectedBuildingIds = selectedItems.map((item) => (item as any).id as number);
    setSelectedBuildingIds(selectedBuildingIds);
  };
  return (
    <div>
      <div style={{ width: "100%", textAlign: "center" }}>
        <h1>Översikt</h1>
      </div>
      <div className="row ">
        <div className="col-lg-3 col-md-12  mt-4">
          <Card style={{ height: "100%" }}>
            <Card.Header>Filter</Card.Header>
            <Card.Body>
              <TreeView
                ref={treeRef}
                dataSource={treeviewData}
                dataStructure="plain"
                keyExpr="id"
                displayExpr="name"
                parentIdExpr="areaId"
                searchEnabled={true}
                searchMode="contains"
                selectByClick={true}
                showCheckBoxesMode="normal"
                selectNodesRecursive={true}
                onSelectionChanged={onSelectionChanged}
              />
            </Card.Body>
          </Card>
        </div>
        <div className="col-lg-9 col-md-12">
          {dashboardData ? (
            <>
              <div className="row">
                <div className={(hasAckBesparingar && dashboardData.hasFunktionsfel ? "col-xxl-6 col-xl-7" : "col-12") + " mt-4"}>
                  <Card style={{ height: "100%" }}>
                    <Card.Header>Status rondering</Card.Header>
                    <Card.Body>
                      {dashboardData.lastPeriodsStatus.length && (
                        <div className="row ">
                          {dashboardData.lastPeriodsStatus.map((p: any, i: number) => (
                            <div key={i} className="col-4">
                              <DashBoardStatusIndicator key={i} showNext={dashboardData.lastPeriodsStatus.length > i + 1} period={p.period} />
                            </div>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
                {dashboardData.hasFunktionsfel ? (
                  <div className="col-xxl-6 col-xl-5 mt-4">
                    <Card style={{ minHeight: 300 }}>
                      <Card.Header>Status funktionskontroller</Card.Header>
                      <Card.Body>
                        <PieChart
                          style={{ fontFamily: "Poppins" }}
                          id="pie"
                          dataSource={dashboardData.fördelningFunktionsfel}
                          palette="Bright"
                          // title="Status funktionskontroller"
                          // onPointClick={pointClickHandler}
                          // onLegendClick={legendClickHandler}

                          customizePoint={customizePoint}
                          customizeAnnotation={customizeText}
                          // customizeText={customizeText}
                        >
                          <Series argumentField="status" valueField="antal">
                            <Label visible={true} position={"inside"} font={{ size: 18, fontfamily: "Poppins" }}>
                              <Connector visible={true} width={1} />
                            </Label>
                          </Series>

                          <Size width="100%" height={300} />
                          <Export enabled={false} />
                          <Legend position={"outside"} horizontalAlignment={"center"} verticalAlignment={"bottom"}></Legend>
                          <CommonAnnotationSettings tooltipEnabled={true} />
                        </PieChart>
                      </Card.Body>
                    </Card>
                  </div>
                ) : (
                  <div className="col-xxl-6 col-xl-5 mt-4">
                    <Card style={{ height: "100%", minHeight: "300px" }}>
                      <Card.Header>Status funktionskontroller</Card.Header>
                      <Card.Body className="align-middle">
                        <div style={{ height: "100%" }} className="col">
                          {/* <div className="row pt-2">
                            <div className="text-center fw-bold">Status funktionskontroller</div>
                          </div> */}
                          <div style={{ height: "100%" }} className="row align-content-center">
                            <div className="text-center">Diagram visas efter att funktionsfel har hittats vid rondering</div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                )}
                {hasAckBesparingar ? (
                  <div className="mt-4">
                    <Card>
                      <Card.Header>Ackumulerade besparingar</Card.Header>
                      <Card.Body>
                        <Chart dataSource={dashboardData.ackBesparingar}>
                          {/* <Title text={"Ackumulerade besparingar (kWh)"}></Title> */}
                          <CommonSeriesSettings argumentField="brytpunktUtetemp" type="line" />
                          <ArgumentAxis title={"Rondering"} tickInterval={1}>
                            {<Label overlappingBehavior="rotate" rotationAngle={45}></Label> /* displayMode="rotate" */}
                          </ArgumentAxis>
                          <ValueAxis title={"kWh"} name="kwh" position="left" valueType={"bignumber"} showZero={true}>
                            <Label format={{ type: "fixedpoint", precision: 0 }}></Label>
                          </ValueAxis>
                          {/* <ValueAxis title={"sek"} name="sek" position="right">
                      <Label format={{ type: "fixedpoint", precision: 0 }}></Label>
                    </ValueAxis> */}
                          <Series key="kwh" argumentField={"p"} valueField="kwh" name="kwh"></Series>
                          {/* <Series key="sek" argumentField={"p"} valueField="sek" name="sek" axis="sek" /> */}
                          <Tooltip enabled={true} shared={true} customizeTooltip={customizeTooltip}></Tooltip>
                          <Export enabled={false} />
                          <Legend visible={false} position={"outside"} horizontalAlignment={"center"} verticalAlignment={"bottom"}></Legend>
                        </Chart>
                      </Card.Body>
                    </Card>
                  </div>
                ) : (
                  <div className="mt-4">
                    <Card>
                      <Card.Header>Ackumulerade besparingar</Card.Header>
                      <Card.Body>
                        <div style={{ height: "100%" }} className="col">
                          {/* <div className="row pt-2">
                            <div className="text-center fw-bold">Ackumulerade besparingar (kWh)</div>
                          </div> */}
                          <div style={{ height: "100%" }} className="row align-content-center">
                            <div className="text-center">Diagram visas efter att åtminstone en rondering har avslutats</div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                )}
                {hasAckSEK && (
                  <div className="mt-4">
                    <Card>
                      <Card.Header>Ackumulerade besparingar flödestaxa (SEK)</Card.Header>
                      <Card.Body>
                        <Chart dataSource={dashboardData.ackBesparingar}>
                          {/* <Title text={"Ackumulerade besparingar flödestaxa (SEK)"}></Title> */}
                          <CommonSeriesSettings argumentField="brytpunktUtetemp" type="line" />
                          <ArgumentAxis title={"Rondering"}>{<Label overlappingBehavior="rotate" rotationAngle={45}></Label> /* displayMode="rotate" */}</ArgumentAxis>
                          {/* <ValueAxis title={"kWh"} name="kwh" position="left" valueType={"bignumber"} showZero={true}>
                      <Label format={{ type: "fixedpoint", precision: 0 }}></Label>
                    </ValueAxis> */}
                          <ValueAxis title={"sek"} name="sek">
                            <Label format={{ type: "fixedpoint", precision: 0 }}></Label>
                          </ValueAxis>
                          {/* <Series key="kwh" argumentField={"p"} valueField="kwh" name="kwh"></Series> */}
                          <Series key="sek" argumentField={"p"} valueField="sek" name="sek" axis="sek" />
                          <Tooltip enabled={true} shared={true} customizeTooltip={customizeTooltip}></Tooltip>
                          <Export enabled={false} />
                          <Legend visible={false} position={"outside"} horizontalAlignment={"center"} verticalAlignment={"bottom"}></Legend>
                        </Chart>
                      </Card.Body>
                    </Card>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>Ingen data</>
          )}
        </div>
      </div>
    </div>
  );
};

export default Start;
